import React, { useEffect, useState } from 'react';
import { DNABox, DNAButton, DNAText, Iffy } from '@alucio/lux-ui'
import { IFieldComponentProps } from '../../ComposableField';
import {
  CRMAccountBadge,
  CRMAttendeeAddress,
  PartialNewAttendee,
  SearchEnterContactInput,
  styles,
} from '../../AttendeeInput';
import colors from '@alucio/lux-ui/src/theming/themes/alucio/colors';
import { Singleton as IndexDbCrm } from 'src/classes/CRM/CRMIndexedDB';
import { CRMAccount } from 'src/classes/CRM/CRMIndexedDBTypes';
import { CRMAccountORM } from 'src/types/orms';
import { useCRMStatus } from 'src/hooks/useCRMStatus';
import { toORMAccount } from 'src/screens/Profile/CRMIntegration';
import { useComposableForm } from 'src/components/CustomFields/ComposableForm';

const CRMAccountSearcher: React.FC<IFieldComponentProps<string | undefined>> = (props) => {
  const { isReadOnly: propIsReadOnly, onChange, value, disabled } = props;
  const isReadOnly = propIsReadOnly || disabled;
  const { canUseMeetingCRMFields, isCRMSyncing } = useCRMStatus();
  const { controlTypeConfig } = useComposableForm()
  const isSimplified = controlTypeConfig?.CRMACCOUNTSEARCHER?.entryType === 'single'

  const onSelectedAccount = (crmAccount: PartialNewAttendee): void => {
    if (crmAccount.crmAccountId) {
      onChange(crmAccount.crmAccountId);
    }
  };

  const onDeleteAccount = (): void => {
    onChange(undefined);
  };

  return (
    <DNABox appearance="col" spacing="md">
      { /* SEARCH INPUT */ }
      <Iffy is={!value && !isReadOnly}>
        <SearchEnterContactInput
          isPrimary={true}
          onNewAttendee={onSelectedAccount}
          placeholder={
            canUseMeetingCRMFields
              ? 'Search accounts'
              : 'Connecting to CRM...'
          }
          isSimplified={isSimplified}
          isDisabled={!canUseMeetingCRMFields}
        />
      </Iffy>
      { /* NO ACCOUNT SELECTED | READ ONLY MODE */ }
      <Iffy is={!value && isReadOnly}>
        <DNAText status="tertiary">No account selected</DNAText>
      </Iffy>
      { /* ACCOUNT */ }
      {value &&
        <CRMAccountRow
          crmAccountId={value}
          onDelete={onDeleteAccount}
          isReadOnly={!!isReadOnly || isCRMSyncing}
          isSimplified={isSimplified}
        />
      }
    </DNABox>
  );
};

interface CRMAccountRow {
  crmAccountId: string
  isReadOnly: boolean
  onDelete: () => void
  isSimplified: boolean
}

const CRMAccountRow = (props: CRMAccountRow) => {
  const { crmAccountId, isReadOnly, onDelete } = props;
  const [account, setAccount] = useState<CRMAccountORM | undefined>();
  const accountId = account?.model.id;

  // GETS THE ACCOUNT ON INDEXDB TO DISPLAY ITS NAME //
  useEffect(() => {
    if (crmAccountId) {
      async function getAccountInIndexDB(): Promise<void> {
        try {
          const account = await IndexDbCrm.getById<CRMAccount>('ACCOUNT', crmAccountId);
          if (account) {
            const config = await IndexDbCrm.getConfig();
            const [accountORM] = toORMAccount([account], config);
            accountORM && setAccount(accountORM);
          }
        } catch (e) {
          console.error(e);
        }
      }
      getAccountInIndexDB();
    }
  }, [crmAccountId]);

  return (
    <DNABox style={!isReadOnly && [{ ...styles.rowsWrapper, borderColor: colors['color-gray-80'] }]}>
      <DNABox style={!isReadOnly && styles.attendeeRow} alignY="center" fill>
        <DNABox alignY="center" spacing="sm" appearance="col" fill>
          <DNAText bold>
            {account?.meta.formattedName || 'Account not found.'}
          </DNAText>
          <Iffy is={accountId}>
            <CRMAttendeeAddress accountId={accountId!} />
          </Iffy>
        </DNABox>
        <DNABox spacing="sm" alignY="center">
          <Iffy is={accountId}>
            <CRMAccountBadge crmAccountId={accountId!} />
          </Iffy>
          <Iffy is={!isReadOnly}>
            <DNAButton
              appearance="ghost"
              status="subtle"
              iconLeft="trash"
              onPress={onDelete}
            />
          </Iffy>
        </DNABox>
      </DNABox>
    </DNABox>
  );
};

export default CRMAccountSearcher;
